import { useCallback, useState } from 'react';

import { AxiosError } from 'axios';
import {
  useAtom,
  useSetAtom,
} from 'jotai';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  cookieJar,
  RegisterV2ErrorResponseT,
  uiRoutes,
  useRegisterV2,
  useValidateIdentity,
  ValidateIdentityErrorResponseT,
} from '@consumer-frontend/nc-api';
import {
  initialAlertBannerProps,
  somethingWentWrongAlertBannerProps,
} from '@consumer-frontend/nc-utils';

import { buildRegisterV2Request } from './buildRegisterV2Request';
import { VerifyIdentityFormValuesT } from './NcVerifyIdentityForm';
import { useApplyImmediately, useApplyRouting } from '../../routing';
import {
  accountIdAtom,
  alertBannerAtom,
  doesPasswordExistAtom,
  hasVerifiedIdentityAtom,
  isReturningCustomerFlowAtom,
  registrationDataAtom,
  unableToContinueReasonAtom,
} from '../../store';

export type UseVerifyIdentitySubmitReturnT = {
  handleSubmit: SubmitHandler<VerifyIdentityFormValuesT>;
  isSubmitting: boolean;
};

/**
 * Calls the `validate_identity` endpoint and routes the customer based on the response. The
 * `isReturningCustomer` flag (driven by the page via `NcVerifyIdentityRouting`) determines the
 * `new_customer` branch: returning customers continue to the phone-number step, while new customers
 * create/update an account via the `account` endpoint.
*/
export const useVerifyIdentitySubmit = (isReturningCustomer: boolean): UseVerifyIdentitySubmitReturnT => {
  const navigate = useNavigate();
  const { handleContinue } = useApplyRouting();
  const { applyImmediately } = useApplyImmediately();

  const [registrationData, setRegistrationData] = useAtom(registrationDataAtom);
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const setHasVerifiedIdentity = useSetAtom(hasVerifiedIdentityAtom);
  const setIsReturningCustomerFlow = useSetAtom(isReturningCustomerFlowAtom);
  const setDoesPasswordExist = useSetAtom(doesPasswordExistAtom);
  const setUnableToContinueReason = useSetAtom(unableToContinueReasonAtom);
  const refreshAccountId = useSetAtom(accountIdAtom);

  const { isMutating: isValidating, validateIdentity } = useValidateIdentity();
  const { isMutating: isCreatingAccount, registerV2 } = useRegisterV2(true);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigateTo = useCallback(
    (to: string) => {
      setIsRedirecting(true);
      navigate(to);
    },
    [navigate],
  );

  const handleSubmit = useCallback<SubmitHandler<VerifyIdentityFormValuesT>>(
    async ({ dateOfBirth, ssn }) => {
      setAlertBanner(initialAlertBannerProps); // Clear any banner from a previous attempt.

      setRegistrationData((currentData) => ({
        ...currentData,
        dateOfBirth: dateOfBirth?.toString() ?? '',
      }));

      let response;

      try {
        response = await validateIdentity({ social_security_number: ssn.replace(/\D/g, '') });
      } catch (error) {
        const { response: errorResponse } = error as AxiosError<ValidateIdentityErrorResponseT>;
        const status = errorResponse?.status;
        const errorType = errorResponse?.data?.error_type;

        if (status === 422 && errorType === 'CACHE_MISS') {
          navigateTo(uiRoutes.apply.basicInfo);

          return;
        }

        if (status === 422 && errorType === 'email_not_verified') {
          navigateTo(uiRoutes.apply.verifyEmail);

          return;
        }

        setAlertBanner(somethingWentWrongAlertBannerProps);

        return;
      }

      if (!response) {
        return;
      }

      // Blocked: SSN not eligible to apply. The header shown depends on whether the customer was
      // in the returning-customer flow (handled by NcUnableToContinuePage via unableToContinueReasonAtom).
      if (!response.ssnApplyEligible) {
        setUnableToContinueReason(isReturningCustomer ? 'unableToVerify' : 'existingEmail');
        navigateTo(uiRoutes.apply.unableToContinue);

        return;
      }

      // Fully authenticated returning customer (secured account, email previously verified).
      if (response.path === 'returning_customer') {
        if (response.accessToken && response.refreshToken) {
          cookieJar.saveTokens(response.accessToken, response.refreshToken);
          refreshAccountId();
        }

        setIsRedirecting(true);

        if (await applyImmediately()) {
          return;
        }

        navigateTo(uiRoutes.apply.verifyContactInfo);

        return;
      }

      if (response.path === 'new_customer') {
        // A returning-flow customer's old account will be archived — treat them as a new customer.
        if (isReturningCustomer) {
          setHasVerifiedIdentity(true);
          setIsReturningCustomerFlow(false);
          setDoesPasswordExist(false);
          navigateTo(uiRoutes.apply.phoneNumber);

          return;
        }

        let accountResponse;

        try {
          accountResponse = await registerV2(buildRegisterV2Request(registrationData, dateOfBirth));
        } catch (error) {
          const { response: errorResponse } = error as AxiosError<RegisterV2ErrorResponseT>;
          const status = errorResponse?.status;
          const errorType = errorResponse?.data?.error_type;

          if (status === 422 && errorType === 'CACHE_MISS') {
            navigateTo(uiRoutes.apply.basicInfo);

            return;
          }

          if (status === 422 && errorType === 'email_not_verified') {
            navigateTo(uiRoutes.apply.verifyEmail);

            return;
          }

          // 409: matched account exists but is not archive-eligible — treat as an existing account.
          if (status === 409) {
            setUnableToContinueReason('existingEmail');
            navigateTo(uiRoutes.apply.unableToContinue);

            return;
          }

          setAlertBanner(somethingWentWrongAlertBannerProps);

          return;
        }

        if (!accountResponse) {
          return;
        }

        // Both 200 (updated) and 201 (created) proceed to the income source page.
        cookieJar.saveTokens(accountResponse.accessToken, accountResponse.refreshToken);
        refreshAccountId();

        setIsRedirecting(true);
        await handleContinue({});

        // Clear registration data, preserving only fields needed downstream.
        const { address } = registrationData;

        setRegistrationData({
          address: address
            ? {
              addressLine1: '',
              addressLine2: '',
              city: '',
              postalCode: '',
              state: address.state,
            }
            : undefined,
          loanAmount: registrationData.loanAmount,
          loanPurpose: registrationData.loanPurpose,
        });
      }
    },
    [
      applyImmediately,
      handleContinue,
      isReturningCustomer,
      navigateTo,
      refreshAccountId,
      registerV2,
      registrationData,
      setAlertBanner,
      setDoesPasswordExist,
      setHasVerifiedIdentity,
      setIsReturningCustomerFlow,
      setRegistrationData,
      setUnableToContinueReason,
      validateIdentity,
    ],
  );

  return { handleSubmit, isSubmitting: isValidating || isCreatingAccount || isRedirecting };
};
